import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {ADropEvent} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDEvents';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {ISheetAPI, ISheetLocation, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';
import _get from 'lodash/get';

export class DnDFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'dnd';
    }

    @Function({
        title: 'DND_SWAP()',
        tip: 'Swaps rows in frame(s) according to a DnD event',
        alias: 'DND_SWAP',
        example: 'DND_SWAP()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'success result',
                tip: 'This function returns operation success state or error',
                type: SheetType.AnyIValue,
            }
    })
    @bind
    public async dndSwap(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        })
            api:ISheetAPI
    ):Promise<ABoolean | AError>
    {
        return this._processDnD(api, (sourceLocation:ISheetLocation,
                                      targetLocation:ISheetLocation,
                                      sourceJson:Array<any>,
                                      targetJson:Array<any>,
                                      sourceRowIndex:number,
                                      targetRowIndex:number,
                                      isSameFrame:boolean,
                                      dropDirection:string):ABoolean | AError =>
        {
            if( !targetJson[targetRowIndex] )
                return AError.fromString('#InvalidTargetPath!');

            const sourceRowJson:any = sourceJson[sourceRowIndex];

            sourceJson[sourceRowIndex] = targetJson[targetRowIndex];
            targetJson[targetRowIndex] = sourceRowJson;

            return ABoolean.true();
        });
    }

    @Function({
        title: 'DND_MOVE()',
        tip: 'Moves row in frame(s) according to a DnD event',
        alias: 'DND_MOVE',
        example: 'DND_MOVE()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'success result',
                tip: 'This function returns operation success state or error',
                type: SheetType.AnyIValue,
            }
    })
    @bind
    public async dndMove(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        })
            api:ISheetAPI
    ):Promise<ABoolean | AError>
    {
        return this._processDnD(api, (sourceLocation:ISheetLocation,
                                      targetLocation:ISheetLocation,
                                      sourceJson:Array<any>,
                                      targetJson:Array<any>,
                                      sourceRowIndex:number,
                                      targetRowIndex:number,
                                      isSameFrame:boolean,
                                      dropDirection:string):ABoolean | AError =>
        {
            if( targetJson.length > 0 && !targetJson[targetRowIndex] )
                return AError.fromString('#InvalidTargetPath!');

            const targetDelta:number = dropDirection === 'left' || dropDirection === 'top' ? 0 : 1;

            const sourceRowJson:any = sourceJson[sourceRowIndex];

            if( sourceRowIndex > targetRowIndex + targetDelta )
            {
                sourceJson.splice(sourceRowIndex, 1);
                targetJson.splice(targetRowIndex + targetDelta, 0, sourceRowJson);
            }
            else
            {
                targetJson.splice(targetRowIndex + targetDelta, 0, sourceRowJson);
                sourceJson.splice(sourceRowIndex, 1);
            }

            return ABoolean.true();
        });
    }

    @Function({
        title: 'DND_COPY()',
        tip: 'Copy row in frame(s) according to a DnD event',
        alias: 'DND_COPY',
        example: 'DND_COPY()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'success result',
                tip: 'This function returns operation success state or error',
                type: SheetType.AnyIValue,
            }
    })
    @bind
    public async dndCopy(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        })
            api:ISheetAPI
    ):Promise<ABoolean | AError>
    {
        return this._processDnD(api, (sourceLocation:ISheetLocation,
                                      targetLocation:ISheetLocation,
                                      sourceJson:Array<any>,
                                      targetJson:Array<any>,
                                      sourceRowIndex:number,
                                      targetRowIndex:number,
                                      isSameFrame:boolean,
                                      dropDirection:string):ABoolean | AError =>
        {
            if( targetJson.length > 0 && !targetJson[targetRowIndex] )
                return AError.fromString('#InvalidTargetPath!');

            const targetDelta:number = dropDirection === 'left' || dropDirection === 'top' ? 0 : 1;

            const sourceRowJson:any = sourceJson[sourceRowIndex];

            targetJson.splice(targetRowIndex + targetDelta, 0, sourceRowJson);

            return ABoolean.true();
        });
    }

    @Function({
        title: 'DND_APPEND()',
        tip: 'Append row in frame(s) according to a DnD event',
        alias: 'DND_APPEND',
        example: 'DND_APPEND()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'success result',
                tip: 'This function returns operation success state or error',
                type: SheetType.AnyIValue,
            }
    })
    @bind
    public async dndAppend(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        })
            api:ISheetAPI
    ):Promise<ABoolean | AError>
    {
        return this._processDnD(api, (sourceLocation:ISheetLocation,
                                      targetLocation:ISheetLocation,
                                      sourceJson:Array<any>,
                                      targetJson:Array<any>,
                                      sourceRowIndex:number,
                                      targetRowIndex:number,
                                      isSameFrame:boolean,
                                      dropDirection:string):ABoolean | AError =>
        {
            if( targetJson.length > 0 && !targetJson[targetRowIndex] )
                return AError.fromString('#InvalidTargetPath!');

            const sourceRowJson:any = sourceJson[sourceRowIndex];

            sourceJson.splice(sourceRowIndex, 1);
            targetJson.push(sourceRowJson);

            return ABoolean.true();
        });
    }

    private async _processDnD(api:ISheetAPI, callback:(sourceLocation:ISheetLocation,
                                                       targetLocation:ISheetLocation,
                                                       sourceJson:Array<any>,
                                                       targetJson:Array<any>,
                                                       sourceRowIndex:number,
                                                       targetRowIndex:number,
                                                       isSameFrame:boolean,
                                                       dropDirection:string) => ABoolean | AError):Promise<ABoolean | AError>
    {
        const frame:IFrame = api.getSheetContext().getEventFrame();

        if( !frame )
            return AError.fromString('#NoEventFrame!');

        const dndSource:IFrame = frame.getCell(ADropEvent.SOURCE_COLUMN, 0) as IFrame;
        const dndTarget:IFrame = frame.getCell(ADropEvent.TARGET_COLUMN, 0) as IFrame;

        if( !dndSource || !dndTarget )
            return AError.fromString('#InvalidEventFrame!');

        const sourceSheetId:AString = dndSource.getCell(ADropEvent.SHEET_ID_COLUMN, 0) as AString;
        const sourceLocationColIndex:ANumber = dndSource.getCell(ADropEvent.LOCATION_COL_INDEX_COLUMN, 0) as ANumber;
        const sourceLocationRowIndex:ANumber = dndSource.getCell(ADropEvent.LOCATION_ROW_INDEX_COLUMN, 0) as ANumber;
        const sourcePath:AString = dndSource.getCell(ADropEvent.PATH_COLUMN, 0) as AString;
        const sourceRowIndex:ANumber = dndSource.getCell(ADropEvent.ROW_INDEX_COLUMN, 0) as ANumber;

        const targetSheetId:AString = dndTarget.getCell(ADropEvent.SHEET_ID_COLUMN, 0) as AString;
        const targetLocationColIndex:ANumber = dndTarget.getCell(ADropEvent.LOCATION_COL_INDEX_COLUMN, 0) as ANumber;
        const targetLocationRowIndex:ANumber = dndTarget.getCell(ADropEvent.LOCATION_ROW_INDEX_COLUMN, 0) as ANumber;
        const targetPath:AString = dndTarget.getCell(ADropEvent.PATH_COLUMN, 0) as AString;
        const targetRowIndex:ANumber = dndTarget.getCell(ADropEvent.ROW_INDEX_COLUMN, 0) as ANumber;
        const dropDirection:AString = dndTarget.getCell(ADropEvent.DROP_DIRECTION_COLUMN, 0) as AString;

        if( !sourceSheetId || !sourceLocationColIndex || !sourceLocationRowIndex || !sourcePath || !sourceRowIndex )
            return AError.fromString('#InvalidSourceData!');

        if( !targetSheetId || !targetLocationColIndex || !targetLocationRowIndex || !targetPath || !targetRowIndex )
            return AError.fromString('#InvalidTargetData!');

        const sourceLocation:SheetLocationImpl = new SheetLocationImpl(
            sourceSheetId.value,
            sourceLocationColIndex.value,
            sourceLocationRowIndex.value
        );
        const targetLocation:SheetLocationImpl = new SheetLocationImpl(
            targetSheetId.value,
            targetLocationColIndex.value,
            targetLocationRowIndex.value
        );
        const isSameFrame:boolean = sourceLocation.isEquals(targetLocation);

        const sourceValue:IValue = api.getSheetContext().getCellValue(sourceLocation);
        const targetValue:IValue = isSameFrame ? sourceValue : api.getSheetContext().getCellValue(targetLocation);

        if( !sourceValue || !Type.isInstanceOf(sourceValue, Type.Frame) )
            return AError.fromString('#InvalidSourceFrame!');

        if( !targetValue || !Type.isInstanceOf(targetValue, Type.Frame) )
            return AError.fromString('#InvalidTargetFrame!');

        const sourceFrameJson:any = JsonUtil.convertFrameToJsonObject(sourceValue as IFrame, true);
        const targetFrameJson:any = isSameFrame ? sourceFrameJson : JsonUtil.convertFrameToJsonObject(targetValue as IFrame, true);

        const sourceJson:Array<any> = sourcePath.value ? _get(sourceFrameJson, sourcePath.value) : sourceFrameJson;
        const targetJson:Array<any> = targetPath.value ? _get(targetFrameJson, targetPath.value) : targetFrameJson;

        if( !sourceJson || !sourceJson[sourceRowIndex.value] )
            return AError.fromString('#InvalidSourcePath!');

        if( !targetJson )
            return AError.fromString('#InvalidTargetPath!');

        if( !Array.isArray(sourceJson) )
            return AError.fromString('#InvalidSourceType!');

        if( !Array.isArray(targetJson) )
            return AError.fromString('#InvalidTargetType!');

        const result:ABoolean | AError = callback(
            sourceLocation,
            targetLocation,
            sourceJson,
            targetJson,
            sourceRowIndex.value,
            targetRowIndex.value,
            isSameFrame,
            dropDirection.value
        );

        if( !result.isError )
        {
            const sourceFrame:IFrame = JsonUtil.convertJsonObjectToFrame(sourceFrameJson);

            await api.getSheetContext().setCellFormula(sourceLocation, sourceFrame);

            if( !isSameFrame )
            {
                const targetFrame:IFrame = JsonUtil.convertJsonObjectToFrame(targetFrameJson);

                await api.getSheetContext().setCellFormula(targetLocation, targetFrame);
            }
        }

        return result;
    }
}
